import { taskModelKind } from '@dbrain/shared';

/**
 * The model per task (MM-009, Phase 13g D-136, D-137): the tasks in their groups, what each task runs on level by
 * level as the gateway resolves it, and a model's name, which comes from the settings and never from code.
 */

/** A model's name: its display name from the settings, else its id (D-137). */
export const modelName = (m: { displayName: string | null; modelId: string }) => m.displayName?.trim() || m.modelId;

// The tasks (MM-009) ------------------------------------------------------------------------------------------

export type TaskGroup = 'main' | 'helpers' | 'search';

/** The tasks in their groups (D-136): the answers people see, the small jobs, document search. */
export const TASK_GROUPS: { id: TaskGroup; tasks: string[] }[] = [
  { id: 'main', tasks: ['main', 'code', 'subagent'] },
  { id: 'helpers', tasks: ['title', 'compaction', 'summary', 'helper'] },
  { id: 'search', tasks: ['embedding', 'rerank', 'ocr'] },
];
export const ROUTE_TASKS = TASK_GROUPS.flatMap((g) => g.tasks);

/** the helper call types the "helper" route serves when they have none of their own (the gateway's order) */
const HELPER_TASKS = new Set(['title', 'summary']);

/** What a task runs on at this level: its own choice, or what it inherits and from where. */
export interface TaskState {
  own: string | null;
  /** the model the task runs on */
  effective: string | null;
  /** what it would run on without a choice of its own here, and why */
  fallback: { modelRef: string | null; source: 'above' | 'helper' | 'main' | 'none' };
}

/**
 * The gateway's resolution (routing.ts), level by level: the task's own route, then (title, summary) the helper
 * route, then the main model; at each step this level before the ones above. Document search never falls back.
 */
export function taskStates(own: Record<string, string>, above: Record<string, string>[] = []): Record<string, TaskState> {
  const first = (task: string, levels: Record<string, string>[]) => levels.find((level) => level[task])?.[task] ?? null;
  const out: Record<string, TaskState> = {};
  for (const task of ROUTE_TASKS) {
    let fallback: TaskState['fallback'] = { modelRef: null, source: 'none' };
    const steps: [string | null, TaskState['fallback']['source']][] = [[first(task, above), 'above']];
    if (HELPER_TASKS.has(task)) steps.push([first('helper', [own, ...above]), 'helper']);
    if (task !== 'main' && taskModelKind(task) === 'chat') steps.push([first('main', [own, ...above]), 'main']);
    for (const [modelRef, source] of steps) {
      if (modelRef) {
        fallback = { modelRef, source };
        break;
      }
    }
    out[task] = { own: own[task] ?? null, effective: own[task] ?? fallback.modelRef, fallback };
  }
  return out;
}

/** The tasks each model serves, in the tasks' order ("Kullanıldığı yer"). */
export function modelUsage(states: Record<string, TaskState>): Map<string, string[]> {
  const usage = new Map<string, string[]>();
  for (const task of ROUTE_TASKS) {
    const ref = states[task]?.effective;
    if (ref) usage.set(ref, [...(usage.get(ref) ?? []), task]);
  }
  return usage;
}

/** The muted words of an inheriting task: "Ana model · X", "Platformun seçimi · X", "Atanmadı". */
export function inheritedLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  nameOf: (modelRef: string) => string | null,
  above: 'platform' | 'tenant' | null,
): (task: string, state: TaskState) => string {
  return (_task, state) => {
    const { modelRef, source } = state.fallback;
    const name = modelRef ? nameOf(modelRef) : null;
    if (!name || source === 'none') return t('models.routes.none');
    if (source === 'main') return t('models.routes.mainModel', { name });
    if (source === 'helper') return t('models.routes.viaHelper', { name });
    return t(`models.routes.above.${above ?? 'platform'}`, { name });
  };
}
